import { uniqBy } from 'lodash-es'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Virtuoso } from 'react-virtuoso'
import type { SubscribePersonalEventsSubscription } from 'src/services/apollo/generated/graphql'
import type {
  Notification,
  NotificationType,
} from 'src/services/apollo/generated/graphql'
import { fetchListMyNotifications } from 'src/services/apollo/notification'
import { usePersonalEventsSubscription } from 'src/services/apollo/subscriptions'
import { getNodesFromConnection } from 'src/utils/graphql'
import useLoadingCallback from 'src/utils/useLoadingCallback'
import Empty from '../Empty'
import { Preloader } from '../Preloader/Preloader'
import NotificationItem from './NotificationItem'

export default function NotificationList(props: {
  id: string
  types: NotificationType[]
  onMarkRead: (types: NotificationType[]) => void
}) {
  const { t } = useTranslation(['common'])
  const [notis, setNotis] = useState<Notification[]>([])
  const [totalCount, setTotalCount] = useState<number>()
  const [hasMore, setHasMore] = useState(true)
  const cursor = useRef<string>(null)
  const pageSize = 5

  const [loading, loadMore] = useLoadingCallback(async () => {
    try {
      const res = await fetchListMyNotifications({
        last: pageSize,
        type: props.types,
        before: cursor.current ?? undefined,
      })
      const connection = res.data?.me?.notifications
      const items = getNodesFromConnection(connection).reverse()
      setNotis(prev => uniqBy([...prev, ...items.filter(i => !!i.id)], 'id'))
      setTotalCount(connection?.totalCount)
      setHasMore(!!connection?.pageInfo?.hasPreviousPage)
      cursor.current = connection?.pageInfo?.startCursor ?? null
    } catch (err) {
      console.error('notificaiton list', err)
    }
  }, [props.types])

  useEffect(() => {
    props.onMarkRead(props.types)
    setNotis([])
    setTotalCount(undefined)
    setHasMore(true)
    cursor.current = null
    loadMore()
  }, [props.types])

  // Subscribe to new notifications via Apollo subscription
  const handleSubscriptionData = useCallback(
    (data: SubscribePersonalEventsSubscription) => {
      const newNotification = data.personalEvents?.newNotification
      if (newNotification && props.types.includes(newNotification.type)) {
        setNotis(prev =>
          uniqBy(
            [newNotification, ...prev].filter(i => !!i.id),
            'id',
          ),
        )
      }
    },
    [props.types],
  )

  usePersonalEventsSubscription({
    onData: handleSubscriptionData,
  })

  const notisToDisplay = notis

  if (notisToDisplay.length === 0) {
    if (loading) return <Preloader />

    return <Empty description={t('common:notification.empty')} />
  }

  return (
    <div
      className="h-[min(384px,calc(100vh-176px))] md:h-[500px]"
      id="notifications-container"
    >
      <Virtuoso
        totalCount={totalCount}
        data={notisToDisplay}
        endReached={() => {
          if (hasMore) {
            loadMore()
          }
        }}
        itemContent={(index, item) => {
          if (!item) return <div className="h-px"></div> // FIXME: should not happen, prevent crash for now
          return <NotificationItem item={item} key={item.id} />
        }}
      />
    </div>
  )
}
